import { useAuth } from "../context/AuthContext";

/**
 * The current user's own Division/Team memberships, shaped for a create-screen
 * Division/Team picker per SEG-S020: show the field whenever the user has any
 * membership at all (required, same treatment as every other field on these
 * forms), and a single-Division tenant (every user's membership reduces to the
 * migration-created Default, with no explicit membership row) sees no field at
 * all since there's nothing to choose. Source is `user/details/` via
 * AuthContext — not the tenant-wide Division/Team lists used elsewhere (e.g.
 * Settings). Note: this was previously gated on >1 membership (silently
 * defaulting at exactly 1); changed 2026-09-10 to match the same fix already
 * applied to the Team field — see [[seg_tech_spec]].
 */
export const useMembershipDivisionTeam = () => {
  const { userDetails } = useAuth();

  const myDivisions = (userDetails?.division ?? []).filter(
    ({ is_active }) => is_active
  );
  const myTeams = (userDetails?.team ?? []).filter(({ is_active }) => is_active);

  const showDivisionField = myDivisions.length > 0;
  const singleDivisionId = myDivisions.length === 1 ? myDivisions[0].id : "";

  const divisionOptions = myDivisions.map(({ id, display_name }) => ({
    label: display_name,
    value: id,
  }));

  const teamsForDivision = (divisionId: any) =>
    myTeams.filter(({ division }) => division === divisionId);

  const teamOptionsFor = (divisionId: any) =>
    teamsForDivision(divisionId).map(({ id, display_name }) => ({
      label: display_name,
      value: id,
    }));

  const singleDivisionTeams = singleDivisionId
    ? teamsForDivision(singleDivisionId)
    : [];
  const singleTeamId =
    singleDivisionTeams.length === 1 ? singleDivisionTeams[0].id : "";

  // Fallback for permission-disabled Division/Team pickers (e.g.
  // mandate.add_division/mandate.add_team missing): pin to the tenant's
  // is_default Division/Team rather than an arbitrary "first option".
  const defaultDivisionId =
    (myDivisions.find(({ is_default }: any) => is_default) ?? myDivisions[0])
      ?.id ?? "";

  const defaultTeamIdFor = (divisionId: any) => {
    const teams = teamsForDivision(divisionId);
    return (
      (teams.find(({ is_default }: any) => is_default) ?? teams[0])?.id ?? ""
    );
  };

  return {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
    defaultDivisionId,
    defaultTeamIdFor,
  };
};
